import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import VerifyOtp from "@/pages/VerifyOtp";
import SetPassword from "@/pages/SetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import Chat from "@/pages/Chat";
import Upgrade from "@/pages/Upgrade";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Voice from "@/pages/Voice";
import Agent from "@/pages/Agent";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import Help from "@/pages/Help";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import Models from "@/pages/Models";
import Privacy from "@/pages/Privacy";
import SharedChat from "@/pages/SharedChat";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return null;
  if (!admin) return <Navigate to="/adminayush/login" replace />;
  return children;
}

function AppRoutes() {
  // Emergent Google auth returns to {origin}/chat#session_id=... — must be caught during render
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<Auth />} />
              {/* Back-compat: old /signup and /login both land on unified /auth */}
              <Route path="/signup" element={<Navigate to="/auth" replace />} />
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              <Route path="/verify" element={<VerifyOtp />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/forgot" element={<ForgotPassword />} />
              <Route path="/reset" element={<ForgotPassword />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/chat/:conversationId" element={<RequireAuth><Chat /></RequireAuth>} />
              <Route path="/upgrade" element={<RequireAuth><Upgrade /></RequireAuth>} />
              <Route path="/billing" element={<RequireAuth><Billing /></RequireAuth>} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/voice" element={<RequireAuth><Voice /></RequireAuth>} />
              <Route path="/agent" element={<RequireAuth><Agent /></RequireAuth>} />
              <Route path="/adminayush/login" element={<AdminLogin />} />
              <Route path="/adminayush" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
              <Route path="/help" element={<Help />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/models" element={<Models />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/share/:token" element={<SharedChat />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <div className="App">
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          <Toaster
            position="bottom-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                color: "hsl(var(--foreground))",
              },
            }}
          />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
